import React from 'react';
import styled from 'styled-components';

import DepartmentsDropdown from '../../CRM/Dialer/DepartmentsDropdown';
import Dial from '../../CRM/Dialer/Dial';
import Timer from '../../Timer';
import Button from '../../Button';
import RoundButton from '../../CRM/Dialer/RoundButton';
import { Types as ButtonTypes } from '../../CRM/Dialer/RoundButton/RoundButton';
import OperatorsDropdown from '../../CRM/Dialer/OperatorsDropdown';

import { VOX_STATES } from '../../../consts/vox';
import { Active as CallIcon } from '@icons/dialer/calls';
import { ValueOf } from '@utils/tsUtils';
import { ContactType } from '../types';
import { DepartmentsDropdownProps } from '@components/CRM/Dialer/DepartmentsDropdown/DepartmentsDropdown';
import { OperatorsDropdownProps } from '@components/CRM/Dialer/OperatorsDropdown/OperatorsDropdown';
import { DialProps } from '@components/CRM/Dialer/Dial/Dial';

export interface ActiveStateProps {
  className?: string;
  state: ValueOf<typeof VOX_STATES>;
  isHold: boolean;
  isDeanonymised: boolean;
  isInterception: boolean;
  isRequestingHelp: boolean;
  isTransfering: boolean;
  isSupervising: boolean;
  isSupervisor: boolean;
  isVoteNeed: boolean;
  serverError: any;
  contact: ContactType | null;
  departments: DepartmentsDropdownProps['departments'];
  operators: OperatorsDropdownProps['operators'];

  onCall: ({ value }: { value: string }) => void;
  onCancelHelpRequest: () => void;
  onConnectSupervisor: (isSupervising: boolean) => void;
  onDeaninomize: (isDeaninomized: boolean) => void;
  onDepartmentsToggle: DepartmentsDropdownProps['onToggle'];
  onHangup: () => void;
  onHold: (isHold: boolean) => void;
  onListen: OperatorsDropdownProps['onListen'];
  onOpenCall: () => void | null;
  onWithdraw: () => void;
  onOperatorsToggle: OperatorsDropdownProps['onToggle'];
  onRequestHelp: OperatorsDropdownProps['onRequestHelp'];
  onTone: DialProps['onTone'];
  onTransfer: ({
    queueId,
    operatorId,
  }: {
    queueId?: number;
    operatorId?: string;
  }) => void;
}

class ActiveState extends React.Component<ActiveStateProps> {
  static defaultProps = {
    className: '',
    state: VOX_STATES.OFFLINE,
    contact: null,
    isHold: false,
    isInterception: false,
    isSupervising: false,
    isSupervisor: false,
    isDeanonymised: false,
    isRequestingHelp: false,
    isVoteNeed: false,
    serverError: false,
    onHangup: Function.prototype,
    onWithdraw: Function.prototype,
    onCancelHelpRequest: Function.prototype,
    onHold: Function.prototype,
    onConnectSupervisor: Function.prototype,
    onTone: Function.prototype,
    onTransfer: Function.prototype,
    onDeaninomize: Function.prototype,
    onCall: Function.prototype,
    onListen: Function.prototype,
    onRequestHelp: Function.prototype,
    onOperatorsToggle: Function.prototype,
    onDepartmentsToggle: Function.prototype,
    onOpenCall: Function.prototype,
  };

  render() {
    const {
      state,
      className,
      isHold,
      isVoteNeed,
      operators,
      departments,
      isInterception,
      isTransfering,
      isSupervising,
      isSupervisor,
      isDeanonymised,
      isRequestingHelp,
      contact,
      serverError,
      onHangup,
      onHold,
      onCall,
      onListen,
      onRequestHelp,
      onTone,
      onTransfer,
      onConnectSupervisor,
      onDeaninomize,
      onWithdraw,
      onCancelHelpRequest,
      onOperatorsToggle,
      onDepartmentsToggle,
      onOpenCall,
    } = this.props;

    return (
      <StyledActiveState className={className}>
        <StyledDescription>
          <StyledStatus>
            <StyledIndicator />
            <StyledTimer />
          </StyledStatus>
          <StyledContact>
            <CallIcon width={19} />
            <StyledName>{contact?.fullName || contact?.number}</StyledName>
          </StyledContact>
        </StyledDescription>
        <StyledControlItems>
          <StyledControls>
            {!isInterception && !isDeanonymised && !isRequestingHelp && (
              <StyledHolder
                active={isHold}
                actionType={ButtonTypes.HOLD}
                onClick={() => onHold(!isHold)}
              />
            )}
            {isRequestingHelp && (
              <RoundButton
                pulsing={isRequestingHelp}
                onClick={onCancelHelpRequest}
                actionType={ButtonTypes.HELPER}
              />
            )}
            {isDeanonymised && !isSupervisor && (
              <RoundButton
                active={isSupervising}
                pulsing={!isSupervising}
                actionType={ButtonTypes.SUPERVISOR}
                onClick={() => onConnectSupervisor(!isSupervising)}
              />
            )}
            <StyledHang>
              <StyledHangupIcon
                actionType={ButtonTypes.END_CALL}
                onClick={onHangup}
              />
              {isVoteNeed && (
                <StyledVoteWarning>
                  Предупредить клиента, что после окончания разговора, он сможет
                  оценить сервис
                </StyledVoteWarning>
              )}
            </StyledHang>
            {!isInterception && <Dial onTone={onTone} />}
            {!isInterception && !isTransfering && (
              <StyledControls>
                <DepartmentsDropdown
                  departments={departments}
                  onChange={onTransfer}
                  onToggle={onDepartmentsToggle}
                />
                <OperatorsDropdown
                  state={state}
                  onCall={onCall}
                  onTransfer={onTransfer}
                  onListen={onListen}
                  onRequestHelp={onRequestHelp}
                  onToggle={onOperatorsToggle}
                  operators={operators}
                  placeholder="На менеджера"
                />
              </StyledControls>
            )}
            {isInterception && isSupervisor && (
              <StyledControls>
                <RoundButton
                  actionType={ButtonTypes.WITHDRAW}
                  onClick={onWithdraw}
                />
                <RoundButton
                  active={isSupervising}
                  actionType={ButtonTypes.MIC}
                  onClick={() => onConnectSupervisor(!isSupervising)}
                />
                <RoundButton
                  active={isDeanonymised}
                  onClick={
                    isDeanonymised
                      ? () => onConnectSupervisor(false)
                      : () => onDeaninomize(!isDeanonymised)
                  }
                  actionType={ButtonTypes.ANONIMIZER}
                />
              </StyledControls>
            )}
            {serverError && (
              <StyledServerError>
                <span>
                  <b>Проблемы с соединением</b>
                  <br />
                  Партнер уже работает над их устранением.
                </span>
              </StyledServerError>
            )}
          </StyledControls>
        </StyledControlItems>
        {onOpenCall && (
          <StyledOpenCall functional onClick={onOpenCall}>
            Перейти в звонок
          </StyledOpenCall>
        )}
      </StyledActiveState>
    );
  }
}

const StyledFlex = styled.div`
  display: flex;
  align-items: center;
`;

const StyledControlItems = styled.div`
  flex: 1;
`;

const StyledActiveState = styled(StyledFlex)`
  height: 100%;
  width: 100%;

  > * + * {
    margin-left: 10px;
  }
`;

const StyledOpenCall = styled(Button)`
  background-image: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.2)
  );
  font-size: 13px;
  color: white;
  font-weight: bold;
  padding: 0 33px;
  min-height: 65px;
`;

const StyledDescription = styled(StyledFlex)`
  color: white;
  min-width: 236px;

  > * + * {
    margin-left: 9px;
  }
`;

const StyledStatus = styled(StyledFlex)`
  min-width: 65px;

  > * + * {
    margin-left: 5px;
  }
`;

const StyledIndicator = styled.div`
  width: 14px;
  height: 14px;
  flex-shrink: 0;
  border-radius: 50%;
  background-image: linear-gradient(to top, #00b0b6, #00e04b);
`;

const StyledTimer = styled(Timer)`
  color: #919499;
  font-size: 14px;
  margin-top: 1px;
  user-select: none;
  font-weight: normal;
`;

const StyledHolder = styled(RoundButton)`
  min-width: 40px;
`;

const StyledHang = styled.div`
  position: relative;
`;

const StyledHangupIcon = styled(RoundButton)`
  border: none;
`;

const StyledVoteWarning = styled.div`
  width: 330px;
  box-shadow: 0 2px 10px 0 rgba(0, 0, 0, 0.1);
  background-color: #f8e71c;
  position: absolute;
  top: 0;
  margin-top: -61px;
  margin-left: -149px;
  border-radius: 6px;
  padding: 11px 10px;
  font-size: 14px;
  z-index: 99;
  text-align: center;

  &:after {
    content: '';
    width: 0;
    height: 0;
    border-left: 10px solid #0000;
    border-right: 10px solid #0000;
    border-top: 10px solid #f8e71c;
    position: absolute;
    bottom: -10px;
    left: 160px;
  }
`;

const StyledContact = styled(StyledFlex)`
  max-width: 220px;

  > * + * {
    margin-left: 7px;
  }
`;

const StyledName = styled.span`
  color: #fff;
  font-size: 14px;
  margin-top: 1px;
  overflow: hidden;
  font-weight: bold;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const StyledControls = styled(StyledFlex)`
  > * + * {
    margin-left: 12px;
  }
`;

const StyledServerError = styled.div`
  color: white;
  height: 65px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: radial-gradient(
    29.25% 121.54% at 47.17% 0%,
    #7e3535 0%,
    transparent 100%
  );
`;

export default ActiveState;
